import { DateTime } from 'luxon'
import type { AvailabilitySlot } from '@gh/common/models'

export function formatClinicalBookingDate(date: Date, zone: string): string {
  return DateTime.fromJSDate(date, { zone }).toLocaleString(DateTime.DATE_MED)
}

/** Scheduled visit date and start time for hub/account list rows (e.g. Tue, Sep 2 · 2:30 PM). */
export function formatClinicalBookingDateTime(date: Date, zone: string): string {
  const dateTime = DateTime.fromJSDate(date, { zone }).setLocale(DateTime.now().locale)
  const datePart = dateTime.toLocaleString({
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
  return `${datePart} · ${formatClinicalTime(date, zone)}`
}

export function formatClinicalTime(
  date: Date,
  zone: string,
  options?: { withTimezone?: boolean },
): string {
  const dateTime = DateTime.fromJSDate(date, { zone }).setLocale(DateTime.now().locale)
  const formattedTime = dateTime.toLocaleString(DateTime.TIME_SIMPLE)
  if (options?.withTimezone && dateTime.offsetNameShort) {
    return `${formattedTime} ${dateTime.offsetNameShort}`
  }
  return formattedTime
}

export function formatClinicalSlotRange(slot: AvailabilitySlot, zone: string): string {
  const start = formatClinicalTime(slot.startTime, zone)
  const end = formatClinicalTime(slot.endTime, zone, { withTimezone: true })
  return `${start} – ${end}`
}

export function formatClinicalCopayCents(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100)
}

export function isSameCalendarDay(date1: Date, date2: Date, zone: string): boolean {
  return DateTime.fromJSDate(date1, { zone }).hasSame(DateTime.fromJSDate(date2, { zone }), 'day')
}

export function formatClinicalCalendarDayKey(date: Date, zone: string): string {
  return DateTime.fromJSDate(date, { zone }).toFormat('yyyy-MM-dd')
}

export function formatClinicalTimezoneHint(zone: string, at: Date = new Date()): string {
  const offsetNameShort = DateTime.fromJSDate(at, { zone }).offsetNameShort
  return offsetNameShort
    ? `Times shown in ${offsetNameShort}.`
    : 'Times shown in your visit timezone.'
}

/** Amber checkout-hold copy for booking flow + hub resume (visit-state TZ). */
export function formatClinicalCheckoutHoldMessage(
  checkoutExpiresAt: Date,
  now: Date,
  zone: string,
): 'expired' | string {
  const remainingMs = checkoutExpiresAt.getTime() - now.getTime()
  if (remainingMs <= 0) {
    return 'expired'
  }
  const remainingMinutes = Math.ceil(remainingMs / (60 * 1000))
  if (remainingMinutes < 60) {
    const noun = remainingMinutes === 1 ? 'minute' : 'minutes'
    return `Your current appointment slot will be held for ${remainingMinutes} more ${noun}.`
  }
  if (isSameCalendarDay(checkoutExpiresAt, now, zone)) {
    const remainingHours = Math.ceil(remainingMinutes / 60)
    const noun = remainingHours === 1 ? 'hour' : 'hours'
    return `Your current appointment slot will be held for ${remainingHours} more ${noun}.`
  }
  const timeWithTz = formatClinicalTime(checkoutExpiresAt, zone, { withTimezone: true })
  const datePart = DateTime.fromJSDate(checkoutExpiresAt, { zone })
    .setLocale('en-US')
    .toLocaleString({
      weekday: 'long',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    })
  return `Your current appointment slot will be held until ${timeWithTz} on ${datePart}.`
}
